/**
 * teamUpdateOverrides — persisted authoritative cache for recently
 * saved team update entries.
 *
 * Same defense as weeklySummaryOverrides, applied to the Team Updates
 * pillar. Cloudflare KV's 60-second read cache can briefly return the
 * pre-save version of meta; the override gives us a deterministic way
 * to show the actual just-saved content until the server catches up,
 * and to stop overriding once the server has newer data.
 *
 * Keyed by teamId ("gtm", "delivery", "product").
 */

const OVERRIDE_TTL_MS = 90_000
const STORAGE_PREFIX = 'tu-override:'

const memoryOverrides = new Map()

function storage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

export function rememberTeamUpdate(teamId, entry) {
  if (!teamId || !entry) return
  const record = { entry, at: Date.now() }
  memoryOverrides.set(teamId, record)
  const s = storage()
  if (s) {
    try {
      s.setItem(STORAGE_PREFIX + teamId, JSON.stringify(record))
    } catch {}
  }
}

/**
 * Returns the authoritative entry for `teamId` if we have a fresh one
 * whose savedAt is newer than the server's. Otherwise returns null and
 * lets the caller fall back to server state.
 */
export function getTeamUpdateAuthoritative(teamId, serverEntry) {
  let record = memoryOverrides.get(teamId)
  if (!record) {
    const s = storage()
    if (s) {
      try {
        const raw = s.getItem(STORAGE_PREFIX + teamId)
        if (raw) {
          record = JSON.parse(raw)
          memoryOverrides.set(teamId, record)
        }
      } catch {}
    }
  }
  if (!record) return null

  // NaN-safe TTL check — see weeklySummaryOverrides for rationale.
  if (!Number.isFinite(record.at) || Date.now() - record.at >= OVERRIDE_TTL_MS) {
    clearTeamUpdateAuthoritative(teamId)
    return null
  }

  const serverAt = serverEntry?.savedAt || 0
  const overrideAt = record.entry?.savedAt || 0
  if (serverAt >= overrideAt) {
    clearTeamUpdateAuthoritative(teamId)
    return null
  }

  return record.entry
}

export function clearTeamUpdateAuthoritative(teamId) {
  memoryOverrides.delete(teamId)
  const s = storage()
  if (s) {
    try { s.removeItem(STORAGE_PREFIX + teamId) } catch {}
  }
}
